import {
  Edit,
  Trash2,
  BadgeCheck,
  Ban,
  Info,
  ChevronDown,
  Eye,
  Hourglass,
} from "lucide-react";
import { useState } from "react";
import { toast } from "react-hot-toast";
import {
  updateJobApplicationStatus,
  deleteJobApplication,
} from "../../../services/jobsServices";
import DeleteMemberModal from "../members/DeleteMemberModal";
import { JobPopup } from "./JobPopup";

const STATUS_OPTIONS = ["pending", "reviewed", "shortlisted", "rejected"];

const statusIcons = {
  pending: <Hourglass size={14} className="text-yellow-500" />,
  reviewed: <Eye size={14} className="text-blue-500" />,
  shortlisted: <BadgeCheck size={14} className="text-green" />,
  rejected: <Ban size={14} className="text-red" />,
};
const statusStyles = {
  pending: "bg-yellow-500 text-white",
  reviewed: "bg-blue-500 text-white",
  shortlisted: "bg-green text-white",
  rejected: "bg-red text-white",
};

const StatusDropdown = ({ currentStatus, onChange }) => {
  const [open, setOpen] = useState(false);

  const handleSelect = (status) => {
    setOpen(false);
    if (status !== currentStatus) {
      onChange(status);
    }
  };

  return (
    <div className="relative inline-block">
      <div
        onClick={() => setOpen((prev) => !prev)}
        className={`text-xs font-semibold rounded-full px-2 py-1 cursor-pointer flex items-center justify-center gap-1 ${statusStyles[currentStatus]}`}
      >
        <span className="capitalize">{currentStatus}</span>
        <ChevronDown size={12} />
      </div>

      {open && (
        <div className="absolute z-10 mt-1 bg-white shadow-md rounded border w-32 flex flex-col">
          {STATUS_OPTIONS.map((status) => (
            <div
              key={status}
              onClick={() => handleSelect(status)}
              className={`px-2 py-[6px] text-xs cursor-pointer flex items-center gap-2 hover:bg-gray-100 ${
                currentStatus === status ? "font-bold" : ""
              }`}
            >
              {statusIcons[status]}
              <span className="capitalize py-1">{status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const JobApplied = ({ applications, refetch, startIndex }) => {
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await updateJobApplicationStatus(id, { status: newStatus });
      toast.success("Application status updated");
      refetch();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const confirmDelete = (application) => {
    setSelectedApplication(application);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      await deleteJobApplication(selectedApplication._id);
      toast.success("Application deleted");
      setShowDeleteModal(false);
      setSelectedApplication(null);
      refetch();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleViewDetails = (application) => {
    setSelectedApplication(application);
    setOpenDetail(true);
  };
  return (
    <div className="bg-white rounded-xl overflow-auto shadow-sm min-h-[300px]">
      <table className="w-full text-sm border border-gray-300 table-auto">
        <thead className="bg-[#4F22D8] text-white">
          <tr>
            <th className="py-3 px-2 text-left font-bold border-r border-gray-400">
              No
            </th>
            <th className="py-3 px-2 text-left font-bold border-r border-gray-400">
              Applicant Name
            </th>
            <th className="py-3 px-2 text-left font-bold border-r border-gray-400">
              Job Title
            </th>
            <th className="py-3 px-2 text-left font-bold border-r border-gray-400">
              Experience
            </th>

            <th className="py-3 px-2 text-left font-bold border-r border-gray-400">
              View
            </th>
            <th className="py-3 px-2 text-left font-bold border-r border-gray-400">
              Status
            </th>
            <th className="py-3 px-2 text-left font-bold border-r border-gray-400">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {applications.length === 0 ? (
            <tr>
              <td colSpan="8" className="text-center py-6 text-gray-500">
                No applications found.
              </td>
            </tr>
          ) : (
            applications.map((application, idx) => (
              <tr
                key={application._id}
                className="border-t border-gray-200 hover:bg-gray-50"
              >
                <td className="px-3 py-2 border-r border-gray-300">
                  {startIndex + idx + 1}
                </td>
                <td className="px-3 py-2 border-r border-gray-300 break-words">
                  {application.fullName}
                </td>
                <td className="px-3 py-2 border-r border-gray-300 break-words">
                  {application.jobId?.title}
                </td>
                <td className="px-3 py-2 border-r border-gray-300">
                  {application.experience} yrs
                </td>

                <td className="px-3 py-2 border-r  border-gray-300">
                  <button
                    onClick={() => handleViewDetails(application)}
                    className="text-xs text-[#4F22D8] flex items-center gap-1"
                  >
                    {" "}
                    <Eye size={15} />{" "}
                    <span className="text-xs font-semibold whitespace-nowrap">
                      View Applicant
                    </span>
                  </button>
                </td>
                <td className="px-3 py-2 border-r border-gray-300">
                  <div className="flex items-center gap-2">
                    <StatusDropdown
                      currentStatus={application.status}
                      onChange={(newStatus) =>
                        handleStatusUpdate(application._id, newStatus)
                      }
                    />
                    <div className="relative group">
                      <Info
                        size={14}
                        className="text-gray-400 hover:text-gray-600"
                      />
                      <div className="absolute z-20 hidden group-hover:block w-max bg-black text-white text-xs px-1.5 py-0.5 rounded top-full left-1/2 transform -translate-x-1/2 mt-1 whitespace-nowrap">
                        Please update status
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2 border-r border-gray-300">
                  <div className="flex items-center gap-2">
                    <div className="w-px h-4 bg-gray-300" />
                    <button
                      onClick={() => confirmDelete(application)}
                      className="text-[#D40404] font-semibold text-xs flex items-center gap-1"
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {selectedApplication && (
        <DeleteMemberModal
          isOpen={showDeleteModal}
          onRequestClose={() => setShowDeleteModal(false)}
          onDelete={handleDelete}
          itemName={`${selectedApplication.fullName}'s application`}
          itemType="application"
        />
      )}

      {openDetail && (
        <JobPopup
          applicationData={selectedApplication}
          isOpen={openDetail}
          onClose={() => setOpenDetail(false)}
        />
      )}
    </div>
  );
};

export default JobApplied;
